import { useMemo, Fragment } from "react";
import classNames from "classnames";

import { LoadingSpinnerIcon } from "../AscIcons";
import { LoadingWoman } from "../AscIllustrations";
import AscHeading from "../AscTypography/AscHeading/AscHeading";
import AscParagraph from "../AscTypography/AscParagraph/AscParagraph";
import styles from "./AscLoading.module.scss";

type AscLoadingType = "default" | "spinner";
export interface AscLoadingProps {
  className?: string;
  type?: AscLoadingType;
  loading: boolean;
  children?: React.ReactNode;
  title?: string;
  description?: string;
  fillWidth?: boolean;
  fillHeight?: boolean;
  fillWidthAndHeight?: boolean;
  loadingIllustration?: React.ReactNode;
}

export default function AscLoading({
  className,
  loading,
  type = "default",
  children,
  title = "Loading...",
  description = "One moment and enjoy your opportunities.",
  fillWidth = false,
  fillHeight = false,
  fillWidthAndHeight = false,
  loadingIllustration = <LoadingWoman />,
}: AscLoadingProps) {
  const content = useMemo(() => {
    switch (type) {
      case "default":
        return (
          <Fragment>
            {loadingIllustration}
            {title ? (
              <AscHeading level={3} className={styles.title}>
                {title}
              </AscHeading>
            ) : null}
            {description ? (
              <AscParagraph className={styles.description}>
                {description}
              </AscParagraph>
            ) : null}
          </Fragment>
        );
      case "spinner":
        return (
          <LoadingSpinnerIcon
            data-testid="asc-loading-spinner-icon"
            className={styles.loadingIcon}
          />
        );
    }
  }, [description, loadingIllustration, title, type]);

  if (!loading) {
    return <Fragment>{children}</Fragment>;
  }

  return (
    <div
      data-testid="asc-loading"
      className={classNames(
        styles.ground,
        {
          [styles.fillWidth]: fillWidth,
          [styles.fillHeight]: fillHeight,
          [styles.fillWidthAndHeight]: fillWidthAndHeight,
        },
        className
      )}
    >
      {content}
    </div>
  );
}
